import {
  analyzeFaultlines,
  buildMarkdown,
  buildShareSummary,
} from '../../lib/faultlines/engine';
import type {
  FaultlinesInput,
  FaultlinesOutput,
  TrustBoundary,
} from '../../lib/faultlines/types';

const form = document.querySelector<HTMLFormElement>('#faultlines-form');
const notesInput = document.querySelector<HTMLTextAreaElement>('#faultlines-notes');
const notesCount = document.querySelector<HTMLSpanElement>('#faultlines-notes-count');
const analyzeButton = document.querySelector<HTMLButtonElement>('#faultlines-analyze');
const resetButton = document.querySelector<HTMLButtonElement>('#faultlines-reset');
const errorEl = document.querySelector<HTMLParagraphElement>('#faultlines-error');
const statusEl = document.querySelector<HTMLParagraphElement>('#faultlines-status');

const exampleSaas = document.querySelector<HTMLButtonElement>('#faultlines-example-saas');
const exampleHybrid = document.querySelector<HTMLButtonElement>(
  '#faultlines-example-hybrid'
);
const exampleMulti = document.querySelector<HTMLButtonElement>(
  '#faultlines-example-multi'
);
const exampleEnterprise = document.querySelector<HTMLButtonElement>(
  '#faultlines-example-enterprise'
);
const exampleHealthcare = document.querySelector<HTMLButtonElement>(
  '#faultlines-example-healthcare'
);
const exampleServerless = document.querySelector<HTMLButtonElement>(
  '#faultlines-example-serverless'
);
const exampleIot = document.querySelector<HTMLButtonElement>('#faultlines-example-iot');
const exampleOss = document.querySelector<HTMLButtonElement>('#faultlines-example-oss');

const resultsEl = document.querySelector<HTMLDivElement>('#faultlines-results');
const meterEl = document.querySelector<HTMLParagraphElement>('#faultlines-meter');
const topList = document.querySelector<HTMLUListElement>('#faultlines-top');
const whyEl = document.querySelector<HTMLParagraphElement>('#faultlines-why');
const boundariesEl = document.querySelector<HTMLUListElement>('#faultlines-boundaries');
const cardsEl = document.querySelector<HTMLDivElement>('#faultlines-cards');
const questionsEl = document.querySelector<HTMLUListElement>('#faultlines-questions');
const exportPreview = document.querySelector<HTMLTextAreaElement>(
  '#faultlines-export-preview'
);
const gameScoreEl = document.querySelector<HTMLParagraphElement>(
  '#faultlines-game-score'
);
const gameTierEl = document.querySelector<HTMLParagraphElement>('#faultlines-game-tier');
const gameBossesEl = document.querySelector<HTMLParagraphElement>(
  '#faultlines-game-bosses'
);
const gameBossesGoalEl = document.querySelector<HTMLParagraphElement>(
  '#faultlines-game-bosses-goal'
);
const gameStatusEl = document.querySelector<HTMLParagraphElement>(
  '#faultlines-game-status'
);
const gameBadgesEl = document.querySelector<HTMLDivElement>('#faultlines-game-badges');
const gameScoreBarEl = document.querySelector<HTMLDivElement>(
  '#faultlines-game-score-bar'
);
const radarEl = document.querySelector<SVGSVGElement>('#faultlines-radar');
const scoreBarsEl = document.querySelector<HTMLDivElement>('#faultlines-score-bars');
const trustDiagramEl = document.querySelector<HTMLDivElement>(
  '#faultlines-trust-diagram'
);
const copyLinkBtn = document.querySelector<HTMLButtonElement>('#faultlines-copy-link');
const copyMarkdownBtn = document.querySelector<HTMLButtonElement>('#faultlines-copy-md');
const downloadJsonBtn = document.querySelector<HTMLButtonElement>(
  '#faultlines-download-json'
);
const copySummaryBtn = document.querySelector<HTMLButtonElement>(
  '#faultlines-copy-summary'
);

const systemType = document.querySelector<HTMLSelectElement>('#faultlines-system-type');
const deployment = document.querySelector<HTMLSelectElement>('#faultlines-deployment');
const exposure = document.querySelector<HTMLSelectElement>('#faultlines-exposure');
const identity = document.querySelector<HTMLSelectElement>('#faultlines-identity');
const multiTenancy = document.querySelector<HTMLSelectElement>(
  '#faultlines-multi-tenancy'
);
const dataSensitivity = document.querySelector<HTMLSelectElement>(
  '#faultlines-data-sensitivity'
);
const changeRate = document.querySelector<HTMLSelectElement>('#faultlines-change-rate');
const privilegedAccess = document.querySelector<HTMLSelectElement>(
  '#faultlines-privileged-access'
);
const secretsHandling = document.querySelector<HTMLSelectElement>('#faultlines-secrets');
const observability = document.querySelector<HTMLSelectElement>(
  '#faultlines-observability'
);
const industry = document.querySelector<HTMLSelectElement>('#faultlines-industry');
const incidentHistory = document.querySelector<HTMLSelectElement>(
  '#faultlines-incident-history'
);

if (
  !form ||
  !notesInput ||
  !notesCount ||
  !analyzeButton ||
  !resetButton ||
  !errorEl ||
  !statusEl ||
  !resultsEl ||
  !meterEl ||
  !topList ||
  !whyEl ||
  !boundariesEl ||
  !cardsEl ||
  !questionsEl ||
  !exportPreview ||
  !gameScoreEl ||
  !gameTierEl ||
  !gameBossesEl ||
  !gameStatusEl ||
  !gameBadgesEl ||
  !copyMarkdownBtn ||
  !downloadJsonBtn ||
  !copySummaryBtn ||
  !systemType ||
  !deployment ||
  !exposure ||
  !identity ||
  !multiTenancy ||
  !dataSensitivity ||
  !changeRate ||
  !privilegedAccess ||
  !secretsHandling ||
  !observability ||
  !industry ||
  !incidentHistory
) {
  throw new Error('Faultlines elements not found.');
}

let lastOutput: FaultlinesOutput | null = null;
let lastMarkdown = '';
let lastSummary = '';

// URL state encoding/decoding
function encodeState(input: FaultlinesInput): string {
  return btoa(JSON.stringify(input));
}

function decodeState(hash: string): FaultlinesInput | null {
  try {
    const decoded = atob(hash);
    return JSON.parse(decoded) as FaultlinesInput;
  } catch {
    return null;
  }
}

function updateUrlHash(input: FaultlinesInput) {
  const encoded = encodeState(stripNotesForShare(input));
  history.replaceState(null, '', `#config=${encoded}`);
}

function loadFromUrlHash(): FaultlinesInput | null {
  const hash = window.location.hash;
  if (!hash.startsWith('#config=')) return null;
  const encoded = hash.slice(8); // Remove '#config='
  const decoded = decodeState(encoded);
  return decoded ? stripNotesForShare(decoded) : null;
}

function stripNotesForShare(input: FaultlinesInput): FaultlinesInput {
  const { notes: _notes, ...rest } = input;
  return rest;
}

const meterBaseClass =
  'rounded-full border border-border/70 bg-bg/70 px-3 py-1 text-xs uppercase tracking-[0.3em]';
const meterStyles = {
  Low: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-500',
  Medium: 'border-amber-500/40 bg-amber-500/10 text-amber-500',
  High: 'border-rose-500/40 bg-rose-500/10 text-rose-500',
} as const;
const scoreBadgeStyles = {
  low: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-500',
  medium: 'border-amber-500/40 bg-amber-500/10 text-amber-500',
  high: 'border-rose-500/40 bg-rose-500/10 text-rose-500',
} as const;
const boundaryBadgeBase =
  'rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.3em]';
const topLineBase =
  'flex items-center justify-between gap-3 rounded-lg border border-border/70 bg-surface/80 px-3 py-2';

function setStatus(message: string) {
  statusEl.textContent = message;
}

function setError(message: string) {
  errorEl.textContent = message;
  errorEl.classList.toggle('hidden', message.length === 0);
}

function updateNotesCount() {
  notesCount.textContent = `${notesInput.value.length}`;
}

function toInput(): FaultlinesInput | null {
  if (
    !systemType.value ||
    !deployment.value ||
    !exposure.value ||
    !identity.value ||
    !multiTenancy.value ||
    !dataSensitivity.value ||
    !changeRate.value ||
    !privilegedAccess.value ||
    !secretsHandling.value ||
    !observability.value
  ) {
    return null;
  }

  return {
    systemType: systemType.value as FaultlinesInput['systemType'],
    deployment: deployment.value as FaultlinesInput['deployment'],
    exposure: exposure.value as FaultlinesInput['exposure'],
    identity: identity.value as FaultlinesInput['identity'],
    multiTenancy: multiTenancy.value as FaultlinesInput['multiTenancy'],
    dataSensitivity: dataSensitivity.value as FaultlinesInput['dataSensitivity'],
    changeRate: changeRate.value as FaultlinesInput['changeRate'],
    privilegedAccess: privilegedAccess.value as FaultlinesInput['privilegedAccess'],
    secretsHandling: secretsHandling.value as FaultlinesInput['secretsHandling'],
    observability: observability.value as FaultlinesInput['observability'],
    notes: notesInput.value.trim() || undefined,
    industry: industry.value
      ? (industry.value as FaultlinesInput['industry'])
      : undefined,
    incidentHistory: incidentHistory.value
      ? (incidentHistory.value as FaultlinesInput['incidentHistory'])
      : undefined,
  };
}

function applyInput(input: FaultlinesInput) {
  systemType.value = input.systemType;
  deployment.value = input.deployment;
  exposure.value = input.exposure;
  identity.value = input.identity;
  multiTenancy.value = input.multiTenancy;
  dataSensitivity.value = input.dataSensitivity;
  changeRate.value = input.changeRate;
  privilegedAccess.value = input.privilegedAccess;
  secretsHandling.value = input.secretsHandling;
  observability.value = input.observability;
  industry.value = input.industry ?? '';
  incidentHistory.value = input.incidentHistory ?? '';
  notesInput.value = input.notes ?? '';
  updateNotesCount();
}

function clearResults() {
  resultsEl.classList.add('hidden');
  meterEl.textContent = '-';
  meterEl.className = `${meterBaseClass} text-muted`;
  topList.innerHTML = '';
  whyEl.textContent = '';
  boundariesEl.innerHTML = '';
  cardsEl.innerHTML = '';
  questionsEl.innerHTML = '';
  exportPreview.value = '';
  setGameEmpty();
  lastOutput = null;
  lastMarkdown = '';
  lastSummary = '';
}

function setGameEmpty() {
  gameScoreEl.textContent = '—';
  gameScoreEl.className = 'text-4xl font-semibold tabular-nums text-muted transition-all';
  gameTierEl.textContent = '';
  gameTierEl.classList.add('hidden');
  gameBossesEl.textContent = '—';
  gameBossesEl.className =
    'mt-3 text-4xl font-semibold tabular-nums text-muted transition-all';
  gameBossesGoalEl?.classList.add('hidden');
  gameStatusEl.textContent = 'Fill in the form to see your risk profile.';
  gameBadgesEl.innerHTML = '';
  if (gameScoreBarEl) {
    gameScoreBarEl.style.width = '0%';
    gameScoreBarEl.className =
      'h-full bg-accent rounded-full transition-all duration-500';
  }
}

function renderGame(output: FaultlinesOutput) {
  const scores = output.faultlines.map((line) => line.score);
  const average =
    scores.length > 0
      ? scores.reduce((total, score) => total + score, 0) / scores.length
      : 0;
  const postureScore = Math.max(0, Math.min(100, Math.round(100 - average)));
  const bosses = scores.filter((score) => score >= 70).length;
  const tier =
    postureScore >= 80
      ? 'Excellent'
      : postureScore >= 65
        ? 'Good'
        : postureScore >= 50
          ? 'Fair'
          : 'Needs work';
  const badges: string[] = [];

  if (output.summary.concentration === 'Low') {
    badges.push('Low risk');
  }
  if (bosses === 0) {
    badges.push('No critical');
  }
  if (postureScore >= 70) {
    badges.push('Strong');
  }
  if (badges.length === 0) {
    badges.push('Room to improve');
  }

  const goalMet =
    output.summary.concentration === 'Low' && bosses === 0 && postureScore >= 70;

  // Update score with color based on value
  const scoreColor =
    postureScore >= 70
      ? 'text-emerald-500'
      : postureScore >= 50
        ? 'text-amber-500'
        : 'text-rose-500';

  gameScoreEl.textContent = `${postureScore}`;
  gameScoreEl.className = `text-4xl font-semibold tabular-nums ${scoreColor} transition-all`;
  gameTierEl.textContent = tier;
  gameTierEl.className = 'text-xs uppercase tracking-[0.3em] text-muted';
  gameTierEl.classList.remove('hidden');

  // Update bosses count
  gameBossesEl.textContent = `${bosses}`;
  gameBossesEl.className =
    bosses > 0
      ? 'mt-3 text-4xl font-semibold tabular-nums text-rose-500 transition-all'
      : 'mt-3 text-4xl font-semibold tabular-nums text-emerald-500 transition-all';
  gameBossesGoalEl?.classList.remove('hidden');

  // Update score bar
  if (gameScoreBarEl) {
    gameScoreBarEl.style.width = `${postureScore}%`;
    const barColor =
      postureScore >= 70
        ? 'bg-emerald-500'
        : postureScore >= 50
          ? 'bg-amber-500'
          : 'bg-rose-500';
    gameScoreBarEl.className = `h-full ${barColor} rounded-full transition-all duration-500`;
  }

  // Update status message
  gameStatusEl.textContent = goalMet
    ? 'Strong posture. Ready for deeper review.'
    : `Target: Low concentration, 70+ score, zero high-risk.`;

  // Update badges
  gameBadgesEl.innerHTML = '';
  badges.forEach((badge) => {
    const span = document.createElement('span');
    const badgeColor = goalMet
      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
      : 'border-accent/30 bg-accent/10 text-accent';
    span.className = `rounded-full border ${badgeColor} px-2 py-1 text-[10px] uppercase tracking-[0.3em]`;
    span.textContent = badge;
    gameBadgesEl.appendChild(span);
  });
}

function renderBoundaries(boundaries: TrustBoundary[]) {
  boundariesEl.innerHTML = '';
  const item = document.createElement('li');
  item.className = 'flex flex-wrap items-center gap-2 text-sm';
  boundaries.forEach((boundary, index) => {
    const span = document.createElement('span');
    span.textContent = boundary.label;
    span.className = boundary.active
      ? `${boundaryBadgeBase} border-accent/40 bg-accent/10 text-accent`
      : `${boundaryBadgeBase} border-border/70 bg-bg/60 text-muted`;
    item.appendChild(span);
    if (index < boundaries.length - 1) {
      const arrow = document.createElement('span');
      arrow.textContent = '→';
      arrow.className = 'text-muted';
      item.appendChild(arrow);
    }
  });
  boundariesEl.appendChild(item);
}

function renderFaultlineCard(output: FaultlinesOutput, container: HTMLElement) {
  container.innerHTML = '';
  output.faultlines.forEach((line) => {
    const card = document.createElement('div');
    card.id = `faultline-${line.id}`;
    const scoreTone =
      line.score >= 70
        ? scoreBadgeStyles.high
        : line.score >= 50
          ? scoreBadgeStyles.medium
          : scoreBadgeStyles.low;
    card.className =
      'section-card border-accent/10 bg-gradient-to-br from-surface/90 via-bg/95 to-accent/10 p-5 shadow-[0_12px_28px_rgba(6,29,60,0.12)] scroll-mt-4';
    card.innerHTML = `
      <div class="flex items-center justify-between gap-3">
        <h4 class="text-base font-medium text-text">${line.title}</h4>
        <span class="rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.3em] ${scoreTone}">
          Score ${line.score}
        </span>
      </div>
      <div class="mt-4 grid gap-4 md:grid-cols-2">
        <div>
          <p class="text-xs uppercase tracking-[0.3em] text-muted">Signals to look for</p>
          <ul class="mt-2 space-y-2 text-sm text-text" data-faultline-signals></ul>
        </div>
        <div>
          <p class="text-xs uppercase tracking-[0.3em] text-muted">First hardening moves</p>
          <ul class="mt-2 space-y-2 text-sm text-text" data-faultline-moves></ul>
        </div>
      </div>
      <p class="mt-4 text-sm text-muted"><span class="text-xs uppercase tracking-[0.3em] text-muted">Common failure mode</span><br />${line.failureMode}</p>
      <p class="mt-3 text-sm text-text"><span class="text-xs uppercase tracking-[0.3em] text-muted">Good enough next step</span><br />${line.nextStep}</p>
    `;

    const signalsList = card.querySelector<HTMLUListElement>('[data-faultline-signals]');
    const movesList = card.querySelector<HTMLUListElement>('[data-faultline-moves]');
    if (signalsList) {
      const signals =
        line.signals.length > 0
          ? line.signals
          : ['No dominant signals from current inputs.'];
      signals.forEach((signal) => {
        const li = document.createElement('li');
        li.textContent = signal;
        signalsList.appendChild(li);
      });
    }
    if (movesList) {
      const moves =
        line.moves.length > 0
          ? line.moves
          : ['Keep the path simple and document assumptions.'];
      moves.forEach((move) => {
        const li = document.createElement('li');
        li.textContent = move;
        movesList.appendChild(li);
      });
    }

    container.appendChild(card);
  });
}

// Radar chart rendering with interactive points
function renderRadarChart(output: FaultlinesOutput) {
  if (!radarEl) return;

  const size = 280;
  const center = size / 2;
  const maxRadius = center - 40;
  const faultlines = output.faultlines;
  const count = faultlines.length;
  const angleStep = (2 * Math.PI) / count;

  // Generate polygon points for the risk shape
  const points = faultlines.map((line, i) => {
    const angle = i * angleStep - Math.PI / 2; // Start from top
    const radius = (line.score / 100) * maxRadius;
    const x = center + radius * Math.cos(angle);
    const y = center + radius * Math.sin(angle);
    return `${x},${y}`;
  });

  // Generate grid circles
  const gridLevels = [25, 50, 75, 100];
  const gridCircles = gridLevels
    .map((level) => {
      const r = (level / 100) * maxRadius;
      return `<circle cx="${center}" cy="${center}" r="${r}" fill="none" stroke="currentColor" stroke-opacity="0.15" stroke-width="1"/>`;
    })
    .join('');

  // Generate axis lines and labels (non-interactive parts)
  const axisLines = faultlines
    .map((line, i) => {
      const angle = i * angleStep - Math.PI / 2;
      const x2 = center + maxRadius * Math.cos(angle);
      const y2 = center + maxRadius * Math.sin(angle);
      const labelX = center + (maxRadius + 25) * Math.cos(angle);
      const labelY = center + (maxRadius + 25) * Math.sin(angle);
      const shortTitle = line.title.split(' ')[0]; // First word only
      return `
        <line x1="${center}" y1="${center}" x2="${x2}" y2="${y2}" stroke="currentColor" stroke-opacity="0.2" stroke-width="1"/>
        <text x="${labelX}" y="${labelY}" text-anchor="middle" dominant-baseline="middle" class="fill-current text-[9px] opacity-60 pointer-events-none">${shortTitle}</text>
      `;
    })
    .join('');

  // Determine fill color based on concentration
  const fillColor =
    output.summary.concentration === 'High'
      ? 'rgba(244, 63, 94, 0.25)'
      : output.summary.concentration === 'Medium'
        ? 'rgba(245, 158, 11, 0.25)'
        : 'rgba(16, 185, 129, 0.25)';

  const strokeColor =
    output.summary.concentration === 'High'
      ? '#f43f5e'
      : output.summary.concentration === 'Medium'
        ? '#f59e0b'
        : '#10b981';

  // Build base SVG
  radarEl.innerHTML = `
    <g class="text-muted">
      ${gridCircles}
      ${axisLines}
    </g>
    <polygon
      points="${points.join(' ')}"
      fill="${fillColor}"
      stroke="${strokeColor}"
      stroke-width="2"
      class="transition-all duration-500 pointer-events-none"
    />
    <g id="radar-interactive-points"></g>
  `;

  // Add interactive points with click handlers and tooltips
  const pointsGroup = radarEl.querySelector('#radar-interactive-points');
  if (pointsGroup) {
    const focusFaultline = (lineId: string) => {
      const card = document.getElementById(`faultline-${lineId}`);
      card?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      card?.classList.add('ring-2', 'ring-accent', 'ring-offset-2');
      setTimeout(
        () => card?.classList.remove('ring-2', 'ring-accent', 'ring-offset-2'),
        2000
      );
    };

    faultlines.forEach((line, i) => {
      const angle = i * angleStep - Math.PI / 2;
      const x = center + maxRadius * Math.cos(angle);
      const y = center + maxRadius * Math.sin(angle);
      const scoreColor =
        line.score >= 70 ? '#f43f5e' : line.score >= 50 ? '#f59e0b' : '#10b981';

      // Create interactive group
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('class', 'cursor-pointer');
      g.setAttribute('role', 'button');
      g.setAttribute('tabindex', '0');
      g.setAttribute('aria-label', `${line.title} score ${line.score}`);

      // Larger invisible hit area
      const hitArea = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      hitArea.setAttribute('cx', x.toString());
      hitArea.setAttribute('cy', y.toString());
      hitArea.setAttribute('r', '12');
      hitArea.setAttribute('fill', 'transparent');

      // Visible point
      const point = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      point.setAttribute('cx', x.toString());
      point.setAttribute('cy', y.toString());
      point.setAttribute('r', '5');
      point.setAttribute('fill', scoreColor);
      point.setAttribute('class', 'transition-all duration-200');

      // Tooltip
      const tooltip = document.createElementNS('http://www.w3.org/2000/svg', 'title');
      tooltip.textContent = `${line.title}: ${line.score} - Click to view details`;

      g.appendChild(hitArea);
      g.appendChild(point);
      g.appendChild(tooltip);

      // Click handler
      g.addEventListener('click', () => {
        focusFaultline(line.id);
      });

      g.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          focusFaultline(line.id);
        }
      });

      // Hover effects
      g.addEventListener('mouseenter', () => {
        point.setAttribute('r', '7');
        point.setAttribute('filter', 'drop-shadow(0 0 4px ' + scoreColor + ')');
      });
      g.addEventListener('mouseleave', () => {
        point.setAttribute('r', '5');
        point.removeAttribute('filter');
      });

      pointsGroup.appendChild(g);
    });
  }
}

// Animated score bars rendering
function renderScoreBars(output: FaultlinesOutput) {
  if (!scoreBarsEl) return;

  scoreBarsEl.innerHTML = '';

  output.faultlines.forEach((line) => {
    const barColor =
      line.score >= 70
        ? 'bg-rose-500'
        : line.score >= 50
          ? 'bg-amber-500'
          : 'bg-emerald-500';

    const bgColor =
      line.score >= 70
        ? 'bg-rose-500/10'
        : line.score >= 50
          ? 'bg-amber-500/10'
          : 'bg-emerald-500/10';

    const bar = document.createElement('div');
    bar.className = 'group';
    bar.innerHTML = `
      <div class="flex items-center justify-between gap-2 mb-1">
        <span class="text-xs text-text truncate flex-1" title="${line.title}">${line.title}</span>
        <span class="text-xs font-medium tabular-nums ${line.score >= 70 ? 'text-rose-500' : line.score >= 50 ? 'text-amber-500' : 'text-emerald-500'}">${line.score}</span>
      </div>
      <div class="h-2 rounded-full ${bgColor} overflow-hidden">
        <div
          class="h-full rounded-full ${barColor} transition-all duration-500 ease-out"
          style="width: ${line.score}%"
        ></div>
      </div>
    `;
    scoreBarsEl.appendChild(bar);
  });
}

// Attack chain definitions - how faultlines cascade into each other
const ATTACK_CHAINS = [
  {
    id: 'edge-to-lateral',
    from: 'public-edge',
    to: 'lateral-movement',
    label: 'Edge breach enables',
    description: 'Compromised edge provides foothold for lateral movement',
  },
  {
    id: 'identity-to-priv',
    from: 'identity-session',
    to: 'privileged-access',
    label: 'Weak identity escalates to',
    description: 'Stolen credentials lead to privileged access abuse',
  },
  {
    id: 'priv-to-secrets',
    from: 'privileged-access',
    to: 'secrets-sprawl',
    label: 'Admin access exposes',
    description: 'Privileged access reveals secrets across systems',
  },
  {
    id: 'secrets-to-lateral',
    from: 'secrets-sprawl',
    to: 'lateral-movement',
    label: 'Leaked secrets enable',
    description: 'Shared credentials allow movement between services',
  },
  {
    id: 'lateral-to-exfil',
    from: 'lateral-movement',
    to: 'data-exfil',
    label: 'Movement reaches',
    description: 'Lateral access leads to data stores',
  },
  {
    id: 'supply-to-misconfig',
    from: 'supply-chain',
    to: 'change-misconfig',
    label: 'CI/CD compromise causes',
    description: 'Compromised pipeline introduces misconfigurations',
  },
  {
    id: 'misconfig-to-edge',
    from: 'change-misconfig',
    to: 'public-edge',
    label: 'Misconfig exposes',
    description: 'Configuration errors open edge vulnerabilities',
  },
  {
    id: 'tenant-to-exfil',
    from: 'multi-tenant',
    to: 'data-exfil',
    label: 'Isolation failure leads to',
    description: 'Cross-tenant access enables data theft',
  },
  {
    id: 'blind-to-lateral',
    from: 'telemetry-blind',
    to: 'lateral-movement',
    label: 'Blind spots hide',
    description: 'Undetected movement due to missing telemetry',
  },
];

// Attack chain visualization
function renderAttackChains(output: FaultlinesOutput) {
  const container = document.getElementById('faultlines-attack-chains');
  if (!container) return;

  container.innerHTML = '';

  // Get scores by faultline id
  const scoreMap = new Map(output.faultlines.map((f) => [f.id, f.score]));

  // Filter to active chains (both ends have score >= 35)
  const activeChains = ATTACK_CHAINS.filter((chain) => {
    const fromScore = scoreMap.get(chain.from) ?? 0;
    const toScore = scoreMap.get(chain.to) ?? 0;
    return fromScore >= 35 || toScore >= 35;
  });

  if (activeChains.length === 0) {
    container.innerHTML = `
      <p class="text-sm text-muted text-center py-4">
        No significant attack chains detected with current configuration.
      </p>
    `;
    return;
  }

  // Group chains by severity
  const criticalChains = activeChains.filter((chain) => {
    const fromScore = scoreMap.get(chain.from) ?? 0;
    const toScore = scoreMap.get(chain.to) ?? 0;
    return fromScore >= 70 || toScore >= 70;
  });

  const warningChains = activeChains.filter((chain) => {
    const fromScore = scoreMap.get(chain.from) ?? 0;
    const toScore = scoreMap.get(chain.to) ?? 0;
    return fromScore < 70 && toScore < 70 && (fromScore >= 50 || toScore >= 50);
  });

  const infoChains = activeChains.filter((chain) => {
    const fromScore = scoreMap.get(chain.from) ?? 0;
    const toScore = scoreMap.get(chain.to) ?? 0;
    return fromScore < 50 && toScore < 50;
  });

  function renderChainGroup(
    chains: typeof ATTACK_CHAINS,
    severity: 'critical' | 'warning' | 'info'
  ) {
    const colors = {
      critical: {
        border: 'border-rose-500/30',
        bg: 'bg-rose-500/5',
        text: 'text-rose-500',
        arrow: 'text-rose-400',
      },
      warning: {
        border: 'border-amber-500/30',
        bg: 'bg-amber-500/5',
        text: 'text-amber-500',
        arrow: 'text-amber-400',
      },
      info: {
        border: 'border-emerald-500/30',
        bg: 'bg-emerald-500/5',
        text: 'text-emerald-500',
        arrow: 'text-emerald-400',
      },
    };
    const c = colors[severity];

    return chains
      .map((chain) => {
        const fromFaultline = output.faultlines.find((f) => f.id === chain.from);
        const toFaultline = output.faultlines.find((f) => f.id === chain.to);
        if (!fromFaultline || !toFaultline) return '';

        const fromShort = fromFaultline.title.split(' ').slice(0, 2).join(' ');
        const toShort = toFaultline.title.split(' ').slice(0, 2).join(' ');

        return `
        <div class="flex items-center gap-2 p-3 rounded-lg border ${c.border} ${c.bg} group cursor-pointer transition-all hover:border-accent/50" data-chain-from="${chain.from}" data-chain-to="${chain.to}">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 text-sm">
              <span class="font-medium text-text truncate">${fromShort}</span>
              <span class="${c.text} text-xs">${fromFaultline.score}</span>
            </div>
          </div>
          <div class="flex flex-col items-center px-2">
            <span class="${c.arrow} text-lg">→</span>
            <span class="text-[9px] text-muted text-center leading-tight max-w-[80px]">${chain.label}</span>
          </div>
          <div class="flex-1 min-w-0 text-right">
            <div class="flex items-center justify-end gap-2 text-sm">
              <span class="${c.text} text-xs">${toFaultline.score}</span>
              <span class="font-medium text-text truncate">${toShort}</span>
            </div>
          </div>
        </div>
      `;
      })
      .join('');
  }

  let html = '';

  if (criticalChains.length > 0) {
    html += `
      <div class="mb-4">
        <p class="text-xs uppercase tracking-[0.3em] text-rose-500 mb-2">Critical Paths</p>
        <div class="space-y-2">${renderChainGroup(criticalChains, 'critical')}</div>
      </div>
    `;
  }

  if (warningChains.length > 0) {
    html += `
      <div class="mb-4">
        <p class="text-xs uppercase tracking-[0.3em] text-amber-500 mb-2">Warning Paths</p>
        <div class="space-y-2">${renderChainGroup(warningChains, 'warning')}</div>
      </div>
    `;
  }

  if (infoChains.length > 0) {
    html += `
      <div>
        <p class="text-xs uppercase tracking-[0.3em] text-muted mb-2">Potential Paths</p>
        <div class="space-y-2">${renderChainGroup(infoChains, 'info')}</div>
      </div>
    `;
  }

  container.innerHTML = html;

  // Add click handlers to navigate to faultlines
  container.querySelectorAll('[data-chain-from]').forEach((el) => {
    el.addEventListener('click', () => {
      const fromId = el.getAttribute('data-chain-from');
      const toId = el.getAttribute('data-chain-to');
      // Scroll to the "from" faultline first
      const fromCard = document.getElementById(`faultline-${fromId}`);
      fromCard?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      fromCard?.classList.add('ring-2', 'ring-accent', 'ring-offset-2');
      setTimeout(() => {
        fromCard?.classList.remove('ring-2', 'ring-accent', 'ring-offset-2');
        // Then highlight the "to" faultline
        const toCard = document.getElementById(`faultline-${toId}`);
        toCard?.classList.add('ring-2', 'ring-amber-500', 'ring-offset-2');
        setTimeout(
          () => toCard?.classList.remove('ring-2', 'ring-amber-500', 'ring-offset-2'),
          2000
        );
      }, 1500);
    });
  });
}

// Interactive trust boundary diagram
function renderTrustDiagram(boundaries: TrustBoundary[]) {
  if (!trustDiagramEl) return;

  trustDiagramEl.innerHTML = '';

  const layers = [
    { id: 'users', label: 'Users', ring: 1 },
    { id: 'edge', label: 'Edge', ring: 2 },
    { id: 'app', label: 'App', ring: 3 },
    { id: 'data', label: 'Data', ring: 4 },
    { id: 'admin', label: 'Admin', ring: 5 },
  ];

  const activeIds = new Set(boundaries.filter((b) => b.active).map((b) => b.id));

  const diagram = document.createElement('div');
  diagram.className = 'relative w-full aspect-square max-w-[280px] mx-auto';

  // Create concentric rings
  layers.forEach((layer, index) => {
    const isActive = activeIds.has(layer.id);
    const size = 100 - index * 18; // Decreasing percentages
    const ring = document.createElement('div');
    ring.className = `absolute rounded-full border-2 transition-all duration-300
      ${isActive ? 'border-accent/60 bg-accent/5' : 'border-border/40 bg-surface/30'}`;
    ring.style.width = `${size}%`;
    ring.style.height = `${size}%`;
    ring.style.left = `${(100 - size) / 2}%`;
    ring.style.top = `${(100 - size) / 2}%`;
    ring.title = `${layer.label}: ${isActive ? 'Active boundary' : 'Inactive'}`;

    // Add label only for outermost ring (above it)
    if (index === 0) {
      const label = document.createElement('span');
      label.className = `absolute text-[10px] uppercase tracking-wider ${isActive ? 'text-accent' : 'text-muted'}`;
      label.style.top = '-18px';
      label.style.left = '50%';
      label.style.transform = 'translateX(-50%)';
      label.textContent = layer.label;
      ring.appendChild(label);
    }

    diagram.appendChild(ring);
  });

  // Add center label for Admin (innermost layer)
  const adminActive = activeIds.has('admin');
  const centerLabel = document.createElement('div');
  centerLabel.className = `absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center`;
  centerLabel.innerHTML = `
    <span class="text-[9px] font-medium uppercase tracking-wider ${adminActive ? 'text-accent' : 'text-muted'}">Admin</span>
  `;
  centerLabel.title = 'Admin plane';
  diagram.appendChild(centerLabel);

  // Add legend
  const legend = document.createElement('div');
  legend.className = 'mt-4 flex flex-wrap justify-center gap-3 text-xs';
  layers.forEach((layer) => {
    const isActive = activeIds.has(layer.id);
    const item = document.createElement('span');
    item.className = `flex items-center gap-1 ${isActive ? 'text-accent' : 'text-muted'}`;
    item.innerHTML = `<span class="w-2 h-2 rounded-full ${isActive ? 'bg-accent' : 'bg-muted/40'}"></span>${layer.label}`;
    legend.appendChild(item);
  });

  trustDiagramEl.appendChild(diagram);
  trustDiagramEl.appendChild(legend);
}

function renderOutput(output: FaultlinesOutput) {
  resultsEl.classList.remove('hidden');
  meterEl.className = `${meterBaseClass} ${meterStyles[output.summary.concentration]}`;
  meterEl.textContent = `Risk concentration: ${output.summary.concentration}`;

  // Render new visualizations
  renderRadarChart(output);
  renderScoreBars(output);
  renderTrustDiagram(output.boundaries);
  renderAttackChains(output);

  topList.innerHTML = '';
  output.summary.topFaultlines.forEach((line) => {
    const li = document.createElement('li');
    const link = document.createElement('a');
    const title = document.createElement('span');
    const score = document.createElement('span');
    link.href = `#faultline-${line.id}`;
    link.className = `${topLineBase} cursor-pointer hover:border-accent/50 transition-colors`;
    title.textContent = line.title;
    score.textContent = `Score ${line.score}`;
    score.className = 'text-xs uppercase tracking-[0.3em] text-muted';
    link.append(title, score);
    li.appendChild(link);
    topList.appendChild(li);
  });
  whyEl.textContent = output.summary.why;
  renderBoundaries(output.boundaries);
  renderFaultlineCard(output, cardsEl);
  renderGame(output);

  questionsEl.innerHTML = '';
  output.questions.forEach((question) => {
    const li = document.createElement('li');
    li.textContent = question;
    questionsEl.appendChild(li);
  });

  lastOutput = output;
  lastMarkdown = buildMarkdown(output);
  lastSummary = buildShareSummary(output);
  exportPreview.value = lastSummary;
}

async function copyToClipboard(value: string) {
  if (!value) return;
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const temp = document.createElement('textarea');
  temp.value = value;
  temp.style.position = 'fixed';
  temp.style.opacity = '0';
  document.body.appendChild(temp);
  temp.select();
  document.execCommand('copy');
  temp.remove();
}

function downloadJson(filename: string, payload: object) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

const presets: Record<string, FaultlinesInput> = {
  // Early-stage SaaS with decent practices - moderate risk (~55)
  saas: {
    systemType: 'saas',
    deployment: 'cloud',
    exposure: 'internet',
    identity: 'oidc',
    multiTenancy: 'multi',
    dataSensitivity: 'moderate',
    changeRate: 'high',
    privilegedAccess: 'individual',
    secretsHandling: 'manager',
    observability: 'basic',
    incidentHistory: 'none',
    notes: 'api gateway, jwt, ci pipeline',
  },
  // Legacy hybrid with significant gaps - higher risk (~40)
  hybrid: {
    systemType: 'internal',
    deployment: 'hybrid',
    exposure: 'mixed',
    identity: 'shared',
    multiTenancy: 'single',
    dataSensitivity: 'high',
    changeRate: 'medium',
    privilegedAccess: 'shared',
    secretsHandling: 'env',
    observability: 'minimal',
    industry: 'b2b',
    incidentHistory: 'some',
    notes: 'vpn, firewall, shared admin, legacy systems',
  },
  // High-stakes multi-tenant finance - higher risk (~35)
  multi: {
    systemType: 'saas',
    deployment: 'cloud',
    exposure: 'internet',
    identity: 'mixed',
    multiTenancy: 'multi',
    dataSensitivity: 'high',
    changeRate: 'high',
    privilegedAccess: 'unknown',
    secretsHandling: 'env',
    observability: 'minimal',
    industry: 'finance',
    incidentHistory: 'some',
    notes: 'tenant isolation, ci, terraform, kubernetes, pci',
  },
  // Well-secured enterprise - low risk (~75)
  enterprise: {
    systemType: 'internal',
    deployment: 'on-prem',
    exposure: 'internal',
    identity: 'saml',
    multiTenancy: 'single',
    dataSensitivity: 'moderate',
    changeRate: 'low',
    privilegedAccess: 'jit',
    secretsHandling: 'vault',
    observability: 'strong',
    industry: 'b2b',
    incidentHistory: 'none',
    notes: 'active directory, pam, siem, zero trust',
  },
  // Regulated healthcare SaaS - moderate-low risk (~62)
  healthcare: {
    systemType: 'saas',
    deployment: 'cloud',
    exposure: 'internet',
    identity: 'oidc',
    multiTenancy: 'single',
    dataSensitivity: 'high',
    changeRate: 'low',
    privilegedAccess: 'jit',
    secretsHandling: 'vault',
    observability: 'strong',
    industry: 'healthcare',
    incidentHistory: 'none',
    notes: 'hipaa, phi, ehr integration, audit logging, encryption',
  },
  // Modern serverless - moderate risk (~50)
  serverless: {
    systemType: 'saas',
    deployment: 'cloud',
    exposure: 'internet',
    identity: 'oidc',
    multiTenancy: 'single',
    dataSensitivity: 'low',
    changeRate: 'high',
    privilegedAccess: 'individual',
    secretsHandling: 'manager',
    observability: 'basic',
    incidentHistory: 'none',
    notes: 'lambda, api gateway, dynamodb, event-driven',
  },
  // IoT edge devices - higher risk (~38)
  iot: {
    systemType: 'iot',
    deployment: 'edge',
    exposure: 'mixed',
    identity: 'shared',
    multiTenancy: 'single',
    dataSensitivity: 'moderate',
    changeRate: 'medium',
    privilegedAccess: 'unknown',
    secretsHandling: 'env',
    observability: 'minimal',
    industry: 'other',
    incidentHistory: 'some',
    notes: 'mqtt, edge devices, firmware updates, constrained devices',
  },
  // Open source project - highest risk (~30)
  oss: {
    systemType: 'saas',
    deployment: 'cloud',
    exposure: 'internet',
    identity: 'passwords',
    multiTenancy: 'multi',
    dataSensitivity: 'moderate',
    changeRate: 'high',
    privilegedAccess: 'shared',
    secretsHandling: 'env',
    observability: 'minimal',
    incidentHistory: 'frequent',
    notes: 'github actions, npm publish, public api, community contributors',
  },
};

notesInput.addEventListener('input', updateNotesCount);
updateNotesCount();
setGameEmpty();

// Real-time scoring: update on any input change
const allInputs = [
  systemType,
  deployment,
  exposure,
  identity,
  multiTenancy,
  dataSensitivity,
  changeRate,
  privilegedAccess,
  secretsHandling,
  observability,
  industry,
  incidentHistory,
  notesInput,
];

function runAnalysis(updateUrl = true) {
  setError('');
  const input = toInput();
  if (!input) {
    // Don't show error for incomplete form during real-time updates
    return;
  }
  const output = analyzeFaultlines(input);
  renderOutput(output);
  if (updateUrl) {
    updateUrlHash(input);
  }
}

// Load from URL hash on page load
const initialState = loadFromUrlHash();
if (initialState) {
  applyInput(initialState);
  // Run analysis without updating URL (it's already correct)
  setTimeout(() => runAnalysis(false), 0);
}

// Debounce for notes input
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
function debouncedAnalysis() {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(runAnalysis, 150);
}

// Add real-time listeners
allInputs.forEach((input) => {
  if (input === notesInput) {
    input.addEventListener('input', debouncedAnalysis);
  } else {
    input.addEventListener('change', runAnalysis);
  }
});

form.addEventListener('submit', (event) => {
  event.preventDefault();
  setError('');
  setStatus('');
  const input = toInput();
  if (!input) {
    setError('Complete the required fields before analyzing.');
    return;
  }
  const output = analyzeFaultlines(input);
  renderOutput(output);
  setStatus('Analysis complete.');
});

resetButton.addEventListener('click', () => {
  form.reset();
  notesInput.value = '';
  updateNotesCount();
  clearResults();
  setStatus('Reset complete.');
  setError('');
});

exampleSaas?.addEventListener('click', () => {
  applyInput(presets.saas);
  runAnalysis();
});

exampleHybrid?.addEventListener('click', () => {
  applyInput(presets.hybrid);
  runAnalysis();
});

exampleMulti?.addEventListener('click', () => {
  applyInput(presets.multi);
  runAnalysis();
});

exampleEnterprise?.addEventListener('click', () => {
  applyInput(presets.enterprise);
  runAnalysis();
});

exampleHealthcare?.addEventListener('click', () => {
  applyInput(presets.healthcare);
  runAnalysis();
});

exampleServerless?.addEventListener('click', () => {
  applyInput(presets.serverless);
  runAnalysis();
});

exampleIot?.addEventListener('click', () => {
  applyInput(presets.iot);
  runAnalysis();
});

exampleOss?.addEventListener('click', () => {
  applyInput(presets.oss);
  runAnalysis();
});

copyLinkBtn?.addEventListener('click', async () => {
  const url = window.location.href;
  await copyToClipboard(url);
  setStatus('Link copied to clipboard.');
});

copyMarkdownBtn.addEventListener('click', async () => {
  if (!lastMarkdown) return;
  await copyToClipboard(lastMarkdown);
  setStatus('Markdown copied.');
});

copySummaryBtn.addEventListener('click', async () => {
  if (!lastSummary) return;
  await copyToClipboard(lastSummary);
  setStatus('Summary copied.');
});

downloadJsonBtn.addEventListener('click', () => {
  if (!lastOutput) return;
  downloadJson('faultlines.json', lastOutput);
  setStatus('JSON download started.');
});
